import React, { useEffect, useRef, useState } from "react";
import styles from "../css/login.module.css";
import {
  MdClose,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
} from "react-icons/md";
import useUsers from "../../../hooks/useUsers";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../context/UIContext";
import { validatePasswordParent } from "../../../util/validate";

const COOLDOWN_SEC = 60;
const DEFAULT_EXPIRES_MIN = 10;

export function ResetPasswordModal({ isOpen, onClose }) {
  const [step, setStep] = useState("request"); // "request" | "verify"

  const [email, setEmail] = useState("");
  const [usernameOptions, setUsernameOptions] = useState([]);
  const [username, setUsername] = useState("");

  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [expiresLeft, setExpiresLeft] = useState(0);
  const [codeBlocked, setCodeBlocked] = useState(false);

  const cooldownTimerRef = useRef(null);
  const expiresTimerRef = useRef(null);

  const { findUsernameByEmail, requestPasswordReset, resetPassword } =
    useUsers();
  const { handleToastCenterTop } = useUIContext();
  const { t } = useTranslation();

  const clearTimers = () => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    if (expiresTimerRef.current) {
      clearInterval(expiresTimerRef.current);
      expiresTimerRef.current = null;
    }
  };

  const resetAll = () => {
    setStep("request");
    setEmail("");
    setUsernameOptions([]);
    setUsername("");
    setCode("");
    setPassword("");
    setPasswordConfirm("");
    setShowPassword(false);
    setInfoMessage("");
    setErrorMessage("");
    setIsSending(false);
    setIsResetting(false);
    setCooldownLeft(0);
    setExpiresLeft(0);
    setCodeBlocked(false);
    clearTimers();
  };

  useEffect(() => {
    if (!isOpen) resetAll();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startCooldown = () => {
    setCooldownLeft(COOLDOWN_SEC);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownLeft((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const startExpiresTimer = (expiresInMinutes) => {
    const total = Math.max(
      1,
      Math.floor((expiresInMinutes || DEFAULT_EXPIRES_MIN) * 60)
    );
    setExpiresLeft(total);
    if (expiresTimerRef.current) clearInterval(expiresTimerRef.current);
    expiresTimerRef.current = setInterval(() => {
      setExpiresLeft((s) => {
        if (s <= 1) {
          clearInterval(expiresTimerRef.current);
          expiresTimerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleLookupUsername = () => {
    setErrorMessage("");
    setInfoMessage("");
    if (!email) {
      setErrorMessage("이메일을 먼저 입력해 주세요.");
      return;
    }
    findUsernameByEmail.mutate(email, {
      onSuccess: (data) => {
        const list = Array.isArray(data) ? data : data ? [data] : [];
        if (list.length === 0) {
          setUsernameOptions([]);
          setErrorMessage("등록된 아이디를 찾을 수 없습니다.");
          return;
        }
        setUsernameOptions(list);
        if (list.length === 1) setUsername(list[0]);
        else setUsername("");
      },
      onError: (error) => {
        setUsernameOptions([]);
        const msg = error?.message || "아이디 조회에 실패했습니다.";
        setErrorMessage(msg);
      },
    });
  };

  const handleSendCode = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    if (!email || !username) {
      setErrorMessage("이메일과 아이디를 모두 입력해 주세요.");
      return;
    }
    if (cooldownLeft > 0 || isSending) return;

    setIsSending(true);
    requestPasswordReset.mutate(
      { email, username },
      {
        onSuccess: (data) => {
          setIsSending(false);
          const msg =
            data?.message ||
            "입력하신 정보로 비밀번호 재설정 메일을 보냈습니다. 메일이 도착하지 않으면 입력 정보와 스팸함을 확인해 주세요.";
          setInfoMessage(msg);
          setStep("verify");
          setCode("");
          setCodeBlocked(false);
          startCooldown();
          startExpiresTimer(data?.expiresInMinutes ?? DEFAULT_EXPIRES_MIN);
        },
        onError: (error) => {
          setIsSending(false);
          setErrorMessage(
            error?.message || "요청 처리 중 오류가 발생했습니다."
          );
        },
      }
    );
  };

  const handleResetPassword = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setErrorMessage("");
    setInfoMessage("");

    if (!/^[0-9]{6}$/.test(code)) {
      setErrorMessage("인증 코드는 숫자 6자리입니다.");
      return;
    }
    if (!validatePasswordParent(password)) {
      setErrorMessage(
        t("common.messages.passwordRule2") ||
          "영문, 숫자, 특수문자 중 최소 2가지 이상 포함하여 8자 이상이어야 합니다."
      );
      return;
    }
    if (password !== passwordConfirm) {
      setErrorMessage(
        t("common.messages.passwordNotMatch") || "비밀번호가 일치하지 않습니다."
      );
      return;
    }

    setIsResetting(true);
    resetPassword.mutate(
      { email, username, password, code },
      {
        onSuccess: () => {
          setIsResetting(false);
          handleToastCenterTop(
            "비밀번호가 변경되었습니다. 새 비밀번호로 로그인해 주세요.",
            3500
          );
          onClose();
          resetAll();
        },
        onError: (error) => {
          setIsResetting(false);
          const msg = error?.message || "비밀번호 변경에 실패했습니다.";
          if (/시도\s*횟수.*초과/.test(msg)) {
            setCodeBlocked(true);
          }
          setErrorMessage(msg);
        },
      }
    );
  };

  if (!isOpen) return null;

  const formatTime = (s) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };

  const closeAndReset = () => {
    onClose();
    resetAll();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <span>{t("common.login.resetPassword") || "비밀번호 재설정"}</span>
          <MdClose
            size={24}
            onClick={closeAndReset}
            style={{ cursor: "pointer" }}
          />
        </div>

        {step === "request" ? (
          <form onSubmit={handleSendCode}>
            <div className={styles.modalContent}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>이메일</label>
                <input
                  className={styles.formInput}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setUsernameOptions([]);
                    setUsername("");
                    setErrorMessage("");
                  }}
                  placeholder="example@email.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className={styles.formGroup}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleLookupUsername}
                  disabled={!email || findUsernameByEmail.isLoading}
                >
                  {findUsernameByEmail.isLoading
                    ? "조회 중..."
                    : "이 이메일로 등록된 아이디 확인"}
                </button>
                <small className="text-muted">
                  여러 아이디가 등록되어 있을 수 있어요. 비밀번호를 바꿀
                  아이디를 선택하세요.
                </small>
              </div>

              {usernameOptions.length > 0 ? (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    아이디 선택 ({usernameOptions.length}개 등록됨)
                  </label>
                  {usernameOptions.length <= 5 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {usernameOptions.map((u) => (
                        <label
                          key={u}
                          style={{
                            cursor: "pointer",
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            padding: "4px 8px",
                          }}
                        >
                          <input
                            type="radio"
                            name="username-pick"
                            value={u}
                            checked={username === u}
                            onChange={() => setUsername(u)}
                            style={{ marginRight: 6 }}
                          />
                          {u}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <select
                      className={styles.formInput}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    >
                      <option value="">선택해 주세요</option>
                      {usernameOptions.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>아이디 (직접 입력)</label>
                  <input
                    className={styles.formInput}
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="아이디를 정확히 입력해 주세요"
                    autoComplete="username"
                  />
                </div>
              )}

              {infoMessage && (
                <div className={styles.success}>{infoMessage}</div>
              )}
              {errorMessage && (
                <div className={styles.error}>{errorMessage}</div>
              )}

              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className="btn btn-sm btn-warning"
                  onClick={closeAndReset}
                  disabled={isSending}
                >
                  {t("common.labels.cancel") || "취소"}
                </button>
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={isSending || !email || !username}
                >
                  {isSending && (
                    <span className="spinner-border spinner-border-sm me-1" />
                  )}
                  인증 메일 보내기
                </button>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div className={styles.modalContent}>
              <div className={styles.success}>
                입력하신 이메일로 6자리 인증 코드를 보냈습니다.
                <br />
                메일이 보이지 않으면 스팸함을 확인해 주세요.
                <br />
                {expiresLeft > 0 ? (
                  <>
                    유효 시간: <strong>{formatTime(expiresLeft)}</strong>
                  </>
                ) : (
                  <strong>
                    코드가 만료되었습니다. 아래 "코드 재요청"을 눌러 주세요.
                  </strong>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>인증 코드 (숫자 6자리)</label>
                <input
                  className={styles.formInput}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                  }
                  disabled={codeBlocked || expiresLeft === 0}
                  placeholder="123456"
                />
                {codeBlocked && (
                  <small className="text-danger">
                    인증 시도 횟수를 초과했습니다. 아래에서 코드를 다시 요청해
                    주세요.
                  </small>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>새 비밀번호</label>
                <div className="input-group">
                  <input
                    className={styles.formInput}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="영문/숫자/특수문자 중 2가지 이상, 8자 이상"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <MdOutlineVisibilityOff size={20} />
                    ) : (
                      <MdOutlineVisibility size={20} />
                    )}
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>새 비밀번호 확인</label>
                <input
                  className={styles.formInput}
                  type={showPassword ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              {errorMessage && (
                <div className={styles.error}>{errorMessage}</div>
              )}

              <div
                className={styles.buttonContainer}
                style={{ flexWrap: "wrap" }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSendCode}
                  disabled={cooldownLeft > 0 || isSending}
                  title={
                    cooldownLeft > 0 ? `${cooldownLeft}초 후 재요청 가능` : ""
                  }
                >
                  {isSending && (
                    <span className="spinner-border spinner-border-sm me-1" />
                  )}
                  {cooldownLeft > 0
                    ? `코드 재요청 (${cooldownLeft}초)`
                    : "코드 재요청"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-warning"
                  onClick={closeAndReset}
                  disabled={isResetting}
                >
                  {t("common.labels.cancel") || "취소"}
                </button>
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={
                    isResetting ||
                    codeBlocked ||
                    expiresLeft === 0 ||
                    code.length !== 6 ||
                    !password ||
                    !passwordConfirm
                  }
                >
                  {isResetting && (
                    <span className="spinner-border spinner-border-sm me-1" />
                  )}
                  비밀번호 변경
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
